// API Types for Lightning Monitor

export type GenerationTier = 'tier0' | 'tier1' | 'tier2' | 'tier3' | 'tier4';

export interface LiveJob {
  job_id: string;
  scan_id: string;
  status: 'pending' | 'processing';
  created_at: string;
  started_at: string | null;
  elapsed_seconds: number | null;
  organization_id: string | null;
  site_id: string | null;
  uvcamp_link: string | null;
  batch_id: string | null;
  source: string | null;
  generation_tier: GenerationTier;
}

// FAL API Usage per endpoint
export interface FalEndpointUsage {
  endpoint: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  direct_cost?: number;
  /** What the backend priced these calls at from a published rate (e.g. per execution second). */
  priced_cost?: number;
}

// Gemini API Usage per model
export interface GeminiModelUsage {
  model: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
}

// API Usage tracking
export interface ApiUsage {
  fal: Record<string, FalEndpointUsage>;
  gemini: Record<string, GeminiModelUsage>;
  totals: {
    fal_total_calls: number;
    fal_total_input_tokens: number;
    fal_total_output_tokens: number;
    gemini_total_calls: number;
    gemini_total_input_tokens: number;
    gemini_total_output_tokens: number;
  };
}

export interface HistoricalJob {
  job_id: string;
  scan_id: string;
  status: 'completed' | 'failed' | 'cancelled';
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error: string | null;
  result: Record<string, unknown> | null;
  organization_id: string | null;
  site_id: string | null;
  uvcamp_link: string | null;
  batch_id: string | null;
  source: string | null;
  generation_tier: GenerationTier;
  // Usage tracking fields
  service_version: string | null;
  fal_usage: Record<string, FalEndpointUsage> | null;
  gemini_usage: Record<string, GeminiModelUsage> | null;
}

export interface HistoryResponse {
  jobs: HistoricalJob[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  /** Search terms this application has no field for. Absent on older servers. */
  ignored_terms?: string[];
}

export interface ScanImage {
  label: string;
  url: string;
  camera?: string | null;
}

export interface ScanImagesResponse {
  scan_id: string;
  expires_in: number;
  angles: ScanImage[];
  corners: ScanImage[];
}

export interface ScanDebugImage {
  label: string;
  url: string;
}

export interface ScanDebugView {
  id: string;
  label: string;
  prompt: string | null;
  prompt_view: string | null;
  outputs: ScanDebugImage[];
  inputs: ScanDebugImage[];
}

export interface ScanDebugAftermarket {
  has_aftermarket: boolean;
  has_aftermarket_rims: boolean;
  has_aftermarket_tires: boolean;
  description: string | null;
}

export interface ScanDebugCoverValidation {
  validated: boolean;
  valid: boolean;
  covers: Record<string, string>;
  failed_covers: string[];
}

export interface ScanDebugResponse {
  scan_id: string;
  expires_in: number;
  storage: string;
  views: ScanDebugView[];
  aftermarket?: ScanDebugAftermarket | null;
  cover_validation?: ScanDebugCoverValidation | null;
}

export interface GenerationLogResponse {
  columns: string[];
  rows: Record<string, string>[];
}

export interface IntermediateGroup {
  id: string;
  label: string;
  prompt: string | null;
  images: ScanDebugImage[];
}

export interface ScanIntermediatesResponse {
  scan_id: string;
  expires_in: number;
  storage: string;
  groups: IntermediateGroup[];
}

export interface ScanMetadataResponse {
  scan_id: string;
  storage: string;
  request_params: Record<string, unknown> | null;
  vehicle_metadata: Record<string, unknown> | null;
}

export interface HealthStatus {
  status: string;
  version: string;
  git_commit?: string | null;
  git_branch?: string | null;
  local_build?: boolean;
  dummy_mode?: boolean;
  active_jobs: number;
  pending_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  max_concurrent: number;
}

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface DateRange {
  from: Date | null;
  to: Date | null;
}

// Batch Run Types
export interface BatchRun {
  batch_id: string;
  filename: string;
  total_scans: number;
  completed: number;
  failed: number;
  in_progress: number;
  pending: number;
  completion_percentage: number;
  success_percentage: number;
  created_at: string;
}

export interface BatchCreateRequest {
  filename: string;
  scan_ids: string[];
}

export interface BatchCreatedResponse {
  batch_id: string;
  filename: string;
  total_scans: number;
  message: string;
}

// Batch History Types
export interface BatchHistoryItem {
  batch_id: string;
  filename: string;
  total_scans: number;
  completed: number;
  failed: number;
  success_percentage: number;
  duration_seconds: number | null;
  created_at: string;
  completed_at: string | null;
  total_cost: number | null;  // Sum of all job costs in the batch
}

export interface BatchHistoryResponse {
  batches: BatchHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Photobooth Types
export interface PhotoboothPromptMetadata {
  background: string;
  floor: string;
  setting_name: string;
}

export interface PhotoboothImage {
  name: string;
  is_default: boolean;
  filename: string;
  prompt_metadata?: PhotoboothPromptMetadata | null;
}

export interface PlateImage {
  name: string;
  filename: string;
}

export const REFERENCE_BANK_VIEWS = [
  'left',
  'right',
  'front_left',
  'front_right',
  'rear_left',
  'rear_right',
  'front',
  'rear',
] as const;

export type ReferenceBankView = (typeof REFERENCE_BANK_VIEWS)[number];

export interface ReferenceBankEntry {
  id: string;
  make: string;
  model: string;
  trim: string;
  year_from: number;
  year_to: number;
  views: ReferenceBankView[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReferenceBankEntryInput {
  make: string;
  model: string;
  trim: string;
  year_from: number;
  year_to: number;
}

// Pricing Settings Types
export interface PricingSetting {
  id: number;
  provider: 'fal' | 'gemini' | 'openai' | 'azure';
  endpoint_or_model: string;
  price_per_call: number | null;
  price_per_input_token: number | null;
  price_per_output_token: number | null;
  updated_at: string | null;
}

export interface PricingSettingInput {
  provider: 'fal' | 'gemini' | 'openai' | 'azure';
  endpoint_or_model: string;
  price_per_call?: number | null;
  price_per_input_token?: number | null;
  price_per_output_token?: number | null;
}

// Statistics Types
export interface DurationStats {
  average: number;
  median: number;
  max: number;
  min: number;
  histogram: HistogramBucket[];
}

export interface CostStats {
  average: number;
  median: number;
  max: number;
  min: number;
  total: number;
  histogram: HistogramBucket[];
}

export interface HistogramBucket {
  range: string;
  count: number;
  rangeStart: number;
  rangeEnd: number;
}

export interface CompletionStats {
  completed: number;
  failed: number;
  cancelled: number;
  total: number;
  completedPercent: number;
  failedPercent: number;
  cancelledPercent: number;
}

/**
 * How often the same scan was submitted more than once over the measured
 * window. Nothing deduplicates a `scan_id` server-side, so a scan resubmitted
 * by a caller shows up as several independent jobs.
 */
export interface DuplicationStats {
  /** History rows carrying a non-empty scan id. */
  submissions: number;
  distinctScans: number;
  /** Distinct scan ids submitted more than once. */
  duplicatedScans: number;
  /** duplicatedScans / distinctScans * 100 */
  duplicatePercent: number;
  /** Highest submission count for any single scan id. */
  maxSubmissions: number;
}

export interface ErrorCategory {
  category: string;
  count: number;
  percent: number;
}

export interface EndpointStats {
  endpoint: string;
  /** `self_hosted` = one of our own model-serving pools (Cloud Run). */
  provider: 'fal' | 'gemini' | 'self_hosted';
  totalCalls: number;
  jobsCompleted: number;
  jobsFailed: number;
  successRate: number;
  avgDuration: number;
}

export interface TierStats {
  tier: GenerationTier;
  count: number;
  percent: number;
  completedCount: number;
  averageCost: number;
  totalCost: number;
}

/** Scans whose request set `use_reference_bank`, from the job result's `ReferenceBank` facet. */
export interface ReferenceBankStats {
  requested: number;
  /** A bank entry matched and its views were used for generation. */
  used: number;
  /** Requested, but no entry matched (or its views could not be loaded). */
  notFound: number;
}

export interface VersionStats {
  version: string;
  duration: DurationStats;
  cost: CostStats;
  completion: CompletionStats;
  errors: ErrorCategory[];
  endpointStats: EndpointStats[];
  tierStats: TierStats[];
  referenceBank?: ReferenceBankStats;
  jobCount: number;
}

export interface StatsData {
  duration: DurationStats;
  cost: CostStats;
  completion: CompletionStats;
  duplication: DuplicationStats;
  errors: ErrorCategory[];
  endpointStats: EndpointStats[];
  tierStats?: TierStats[];
  referenceBank?: ReferenceBankStats;
  versions: string[];
  versionStats: VersionStats[];
  totalJobs: number;
  dateRange: DateRange;
}
